import { message, notification } from "antd";
import { ofType } from "redux-observable";
import { concat, EMPTY, of } from "rxjs";
import { catchError, switchMap, map } from "rxjs/operators";
import { FolderService } from "../../services/FolderService";
import projectsService, {
  mapProjectResponseToProject,
  mapToProject,
  ProjectResponse,
} from "../../services/projectsApi";
import { startLoading, stopLoading } from "../loading";
import type { RootEpic } from "../types";
import { projectActions } from "./projectSlice";
import { uploadFileActions } from "../uploadFiles/uploadFileSlice";


export const getFolderRootIdByProjectIdEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(projectActions.getFolderRootId.type),
    switchMap((action: any) => {
      const { projectId } = action.payload;
      return concat(
        of(startLoading({ key: "getFolderRootId" })),
        FolderService.Get.getFolderRootIdByProjectId(projectId).pipe(
          switchMap((res) => [projectActions.setFolderRootId(res)]),
          catchError((err) => {
            console.error(err.message || "Error getting folder");
            return of(
              projectActions.getFolderRootIdFailure(
                err.message || "Error getting folder"
              )
            );
          })
        ),
        of(stopLoading({ key: "getFolderRootId" }))
      );
    })
  );

export const getFolderRootIdWithCreateIfNotExistsEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(projectActions.getFolderRootIdWithCreateIfNotExists.type),
    switchMap((action: any) => {
      const { projectId, companyId } = action.payload;
      return concat(
        of(startLoading({ key: "getFolderRootId" })),
        FolderService.Get.getFolderRootIdByProjectId(projectId).pipe(
          switchMap((res) => {
            // Nếu có rootId thì return luôn
            if (res && res.trim()) {
              return [projectActions.setFolderRootId(res)];
            } else {
              // Nếu không có rootId thì tạo root folder
              const rootFolderData = {
                name: "root",
                type: "folder",
              };

              return FolderService.Post.createFolderRootProject(
                rootFolderData,
                String(projectId),
                undefined,
                companyId
              ).pipe(
                switchMap((createRes: any) => {
                  // Lấy id từ response của create API
                  const rootId = createRes?.id || createRes?._id || createRes;
                  notification.success({
                    message: "Welcome!",
                    description: "Your workspace is empty. Start by uploading a file or creating a folder."
                  });
                  return [projectActions.createFolderRootProjectSuccess(String(rootId))];
                }),
                catchError((createErr: any) => {
                  console.error(createErr.message || "Error creating root folder");
                  return of(
                    projectActions.createFolderRootProjectFailure(
                      createErr.message || "Error creating root folder"
                    )
                  );
                })
              );
            }
          }),
          catchError(() => {
            // Nếu API getFolderRootId fail, có thể là do project chưa có root folder
            // Thử tạo root folder luôn
            const rootFolderData = {
              name: "root",
              type: "folder",
              labelCode: "",
              children: []
            };
            
            const companyId = localStorage.getItem("companyId") || "";
            
            return FolderService.Post.createFolderRootProject(
              rootFolderData,
              String(projectId),
              undefined,
              companyId
            ).pipe(
              switchMap((createRes: any) => {
                const rootId = createRes?.id || createRes?._id || createRes;
                                  notification.success({
                    message: "Welcome!",
                    description: "Your workspace is empty. Start by uploading a file or creating a folder."
                  });
                return [projectActions.createFolderRootProjectSuccess(String(rootId))];
              }),
              catchError((createErr: any) => {
                console.error(createErr.message || "Error creating root folder");
                return of(
                  projectActions.createFolderRootProjectFailure(
                    createErr.message || "Error creating root folder"
                  )
                );
              })
            );
          })
        ),
        of(stopLoading({ key: "getFolderRootId" }))
      );
    })
  );

export const getProjectsEpic$: RootEpic = (action$, state$) => {
  return action$.pipe(
    ofType(projectActions.getProjects.type),
    switchMap((action: any) => {
      const currentState = state$.value.project;
      const { forceRefresh = false, page, pageSize } = action.payload || {};

      // Kiểm tra cache
      const now = Date.now();
      const cacheValid =
        currentState.lastProjectsFetch &&
        now - currentState.lastProjectsFetch < currentState.cacheExpiry;

      if (cacheValid && !forceRefresh && currentState.projects.length > 0) {
        return EMPTY;
      }

      return projectsService.getProjects({ page, pageSize }).pipe(
        switchMap((resp: any) => {
          const mappedProjects = (resp.projects ?? resp).map(mapToProject);
          return [
            projectActions.setProjects(mappedProjects),
            projectActions.setProjectsPagination({
              page: resp.page,
              pageSize: resp.pageSize,
              total: resp.totalCount, // <-- sửa lại thành totalCount
            }),
          ];
        }),
        catchError((err) => {
          console.error(err.message || "Error loading projects");
          return of(
            projectActions.getProjectsFailure(
              err.message || "Error loading projects"
            )
          );
        })
      );
    })
  );
};

export const filterProjectsEpic$: RootEpic = (action$) =>
  action$.pipe(
    ofType(projectActions.filterProjects.type),
    switchMap((action: any) => {
      const filterParams = action.payload;

      return projectsService.filterProjects(filterParams).pipe(
        switchMap((resp: any) => {
          const mappedProjects = resp.projects.map(mapToProject);
          return [
            projectActions.setFilteredProjects({
              projects: mappedProjects,
              totalCount: resp.totalCount,
              page: resp.page,
              pageSize: resp.pageSize,
            }),
          ];
        }),
        catchError((err) => {
          console.error(err.message || "Error filtering projects");
          return of(
            projectActions.filterProjectsFailure(
              err.message || "Error filtering projects"
            )
          );
        })
      );
    })
  );

export const getProjectsByCompanyIdEpic$: RootEpic = (action$: any) =>
  action$.pipe(
    ofType(projectActions.getProjectsByCompanyIdRequest.type),
    switchMap((action: any) => {
      const companyId = action.payload;
      return projectsService.getProjectsByCompanyId(companyId).pipe(
        map((resp: ProjectResponse[]) => {
          const mapped = resp.map(mapProjectResponseToProject);
          return projectActions.getProjectsByCompanyIdSuccess(mapped);
        }),
        catchError((err) => {
          const msg = err?.message || "Error fetching projects by company";
          console.error(msg, err);
          return of(projectActions.getProjectsByCompanyIdFailure(msg));
        })
      );
    })
  );

export const projectEpics = [
  getFolderRootIdByProjectIdEpic$,
  getFolderRootIdWithCreateIfNotExistsEpic$,
  getProjectsEpic$,
  filterProjectsEpic$,
  getProjectsByCompanyIdEpic$,
];
